/**
 * Global search — the first frontend client for GET /api/v1/search.
 *
 * Built for the `@`-mention picker: one flat ranked list across courses,
 * programs, assignments, people, modules and sections. `types` goes over the
 * wire as CSV because that is what the Marshmallow query schema parses.
 */
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  CourseHitPayload,
  ModuleHitPayload,
  ProgramHitPayload,
  SearchHit,
  SearchResponseData,
  SearchResultType,
  UserHitPayload,
} from '../types/search';

interface Envelope<T> {
  status: string;
  data: T;
  message?: string;
}

/** Max the backend will return regardless of what we ask for. */
export const SEARCH_MAX_LIMIT = 20;

export const searchEntities = async (
  q: string,
  types: SearchResultType[],
  limit = 8
): Promise<SearchHit[]> => {
  const res = await api.get<Envelope<SearchResponseData>>('/search', {
    params: { q, types: types.join(','), limit: Math.min(limit, SEARCH_MAX_LIMIT) },
  });
  return res.data.data.results;
};

export const searchKeys = {
  all: ['search'] as const,
  entities: (q: string, types: SearchResultType[], limit: number) =>
    ['search', 'entities', q, types.join(','), limit] as const,
};

/**
 * Type-ahead search. Debouncing is the caller's job (the mention popover
 * debounces the raw token before it reaches here), so this only gates on a
 * non-empty query and the caller's own `enabled` flag.
 */
export function useEntitySearch(
  q: string,
  types: SearchResultType[],
  enabled = true,
  limit = 8
) {
  return useQuery({
    queryKey: searchKeys.entities(q, types, limit),
    queryFn: () => searchEntities(q, types, limit),
    enabled: q.length >= 1 && enabled && types.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

/** The one line a hit shows in a picker — its name, whatever the type calls it. */
export function searchHitLabel(hit: SearchHit): string {
  const payload = hit.payload as Partial<
    CourseHitPayload & ProgramHitPayload & UserHitPayload & ModuleHitPayload
  >;
  return payload.title || payload.name || 'Untitled';
}

/** The quieter second line: an email, a subtitle, or nothing. */
export function searchHitSublabel(hit: SearchHit): string | null {
  if (hit.type === 'user') return (hit.payload as UserHitPayload).email;
  const payload = hit.payload as Partial<CourseHitPayload & ModuleHitPayload>;
  return payload.subtitle || null;
}
